// Adapted from https://beui.dev/r/center-morph-modal; MIT notice in LICENSE.txt.
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";

/** Controlled BeUI center-unfold presentation, adapted to a native modal dialog.
 * The browser supplies background inertness, Escape and focus return. Explicit Tab
 * wrapping prevents Chromium's native cycle from briefly focusing the page body. */
export function Modal({ label, onClose, children, dismissible = true, className }: { label: string; onClose: () => void; children: ReactNode; dismissible?: boolean; className?: string }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    const dialog = dialogRef.current;
    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    dialog?.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog?.close();
      document.body.style.overflow = previousOverflow;
      if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);

  function containTab(event: KeyboardEvent<HTMLDialogElement>) {
    // Portaled source dialogs keep their own focus and Escape handling.
    if (event.target instanceof Element && event.target.closest("dialog") !== event.currentTarget) return;
    if (event.key !== "Tab") return;
    const dialog = event.currentTarget;
    const controls = Array.from(dialog.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]',
    )).filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!first) { event.preventDefault(); dialog.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }

  return <dialog ref={dialogRef} aria-label={label} className={["legal-reader-dialog",className].filter(Boolean).join(" ")} onCancel={(event) => { if (event.target !== event.currentTarget) return; event.preventDefault(); if (dismissible) onClose(); }}
    onKeyDown={containTab}
    onClick={(event) => { if (dismissible && event.target === event.currentTarget) onClose(); }}>
    <motion.div className="legal-reader-panel" initial={reduce ? false : { clipPath: "inset(48% 48% 48% 48% round 20px)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0% round 20px)" }}
      transition={reduce ? { duration: 0 } : { duration: 0.43, ease: [0.2, 0, 0.2, 1] }}>
      {children}
    </motion.div>
  </dialog>;
}
